/**
 * The rollout settings' shape and parser: who is emailed (src/statements/send/rollout.ts
 * decides it per statement). Here so the admin panel's API (shared/statementAdmin.ts) and
 * the sender check a setting the same way. No imports: the app and the API share it.
 */

export interface RolloutSettings {
  /** Only the recipients in `pilot` are emailed, whatever else is set. */
  pilotOnly: boolean
  /** FCC keys of the pilot recipients (managers and supervisors, by the spec). */
  pilot: string[]
  /** Employee cards (Version A): none, the listed departments, or everyone. */
  employees: 'off' | 'departments' | 'all'
  /** Departments whose employees are emailed when `employees` is 'departments'. */
  departments: string[]
}

export const NOTHING: RolloutSettings = { pilotOnly: true, pilot: [], employees: 'off', departments: [] }

/** Parse saved settings, refusing anything it does not understand rather than guessing. */
export function parseSettings(raw: unknown): RolloutSettings {
  if (raw === null || raw === undefined) return NOTHING
  const o = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Record<string, unknown>
  if (typeof o !== 'object' || o === null || Array.isArray(o)) throw new Error('Rollout settings must be an object.')
  const strings = (v: unknown, name: string) => {
    if (v === undefined) return []
    if (!Array.isArray(v) || v.some((x) => typeof x !== 'string')) throw new Error(`Rollout setting "${name}" must be a list of strings.`)
    return v as string[]
  }
  const employees = o.employees ?? 'off'
  if (employees !== 'off' && employees !== 'departments' && employees !== 'all')
    throw new Error(`Rollout setting "employees" must be off, departments or all (got ${JSON.stringify(employees)}).`)
  if (o.pilotOnly !== undefined && typeof o.pilotOnly !== 'boolean') throw new Error('Rollout setting "pilotOnly" must be true or false.')
  const unknown = Object.keys(o).filter((k) => !['pilotOnly', 'pilot', 'employees', 'departments'].includes(k))
  if (unknown.length) throw new Error(`Unknown rollout setting(s): ${unknown.join(', ')}.`)
  return {
    pilotOnly: (o.pilotOnly as boolean | undefined) ?? true,
    pilot: strings(o.pilot, 'pilot'),
    employees,
    departments: strings(o.departments, 'departments'),
  }
}
